import { useState, useEffect } from 'react';
import axios from 'axios';
import MainNav from '../components/MainNav';
import MainFooter from '../components/MainFooter';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, MapPin, Clock, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunityEventsPage = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await axios.get(`${API}/events`);
            setEvents(response.data);
        } catch (error) {
            console.error('Error fetching events:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const upcomingEvents = events.filter(e => new Date(e.date) >= new Date());
    const pastEvents = events.filter(e => new Date(e.date) < new Date());

    return (
        <div className="min-h-screen bg-[#3D4435]">
            <MainNav />
            
            <main className="pt-32 pb-24 px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-4">
                            In The Community
                        </p>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white font-['Playfair_Display'] mb-6">
                            Events
                        </h1>
                        <p className="text-[#A0A0A0] text-lg max-w-2xl mx-auto">
                            Join us at our gatherings, workshops, and community events
                        </p>
                    </div>

                    {/* R&R Saturdays Feature */}
                    <Card className="bg-gradient-to-br from-[#4A5240] to-[#3D4435] border-[#C9A227]/30 mb-12">
                        <CardContent className="p-8 lg:p-12">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                                <div>
                                    <p className="text-[#C9A227] text-sm uppercase tracking-[0.2em] font-medium mb-3">
                                        Monthly Gathering
                                    </p>
                                    <h2 className="text-3xl font-bold text-white font-['Playfair_Display'] mb-4">
                                        R&R Saturdays
                                    </h2>
                                    <p className="text-[#A0A0A0] mb-6 leading-relaxed">
                                        Every 1st Saturday of the month, we gather for connection, introspection, and community. These sessions provide a safe space for brothers to share, listen, and grow together.
                                    </p>
                                    <div className="flex flex-wrap gap-4">
                                        <div className="flex items-center gap-2 text-white">
                                            <Calendar className="w-5 h-5 text-[#C9A227]" />
                                            <span>1st Saturday Monthly</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-white">
                                            <Clock className="w-5 h-5 text-[#C9A227]" />
                                            <span>5:30 PM - 8:00 PM</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="text-center lg:text-right">
                                    <Button
                                        size="lg"
                                        className="bg-[#C9A227] hover:bg-[#B8922A] text-[#3D4435] font-semibold"
                                        onClick={() => window.open('https://www.recoveredbrother.com/', '_blank')}
                                    >
                                        Register for Next Session
                                        <ExternalLink className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Upcoming Events */}
                    <section className="mb-16">
                        <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8 flex items-center gap-3">
                            <Calendar className="w-6 h-6 text-[#C9A227]" />
                            Upcoming Events
                        </h2>
                        
                        {loading ? (
                            <div className="text-center py-12">
                                <div className="text-[#A0A0A0] animate-pulse">Loading events...</div>
                            </div>
                        ) : upcomingEvents.length === 0 ? (
                            <Card className="bg-[#4A5240] border-[#5A6350]">
                                <CardContent className="p-12 text-center">
                                    <Calendar className="w-12 h-12 text-[#666] mx-auto mb-4" />
                                    <p className="text-[#A0A0A0]">
                                        No upcoming events scheduled. Check back soon or join our R&R Saturdays!
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {upcomingEvents.map((event) => (
                                    <Card key={event.id} className="bg-[#4A5240] border-[#5A6350] hover:border-[#C9A227]/50 transition-all">
                                        <CardContent className="p-6">
                                            <div className="flex items-start gap-4">
                                                <div className="w-16 h-16 rounded-lg bg-[#C9A227]/10 flex flex-col items-center justify-center text-[#C9A227] flex-shrink-0">
                                                    <span className="text-xs font-medium">
                                                        {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                                                    </span>
                                                    <span className="text-2xl font-bold leading-none">
                                                        {new Date(event.date).getDate()}
                                                    </span>
                                                </div>
                                                <div className="flex-1">
                                                    <h3 className="text-white font-semibold text-lg mb-2">{event.title}</h3>
                                                    {event.description && (
                                                        <p className="text-[#A0A0A0] text-sm mb-3 line-clamp-2">{event.description}</p>
                                                    )}
                                                    <div className="flex flex-wrap gap-3 text-sm text-[#666]">
                                                        <span className="flex items-center gap-1">
                                                            <Calendar className="w-4 h-4" />
                                                            {formatDate(event.date)}
                                                        </span>
                                                        {event.location && (
                                                            <span className="flex items-center gap-1">
                                                                <MapPin className="w-4 h-4" />
                                                                {event.location}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Past Events */}
                    {pastEvents.length > 0 && (
                        <section>
                            <h2 className="text-2xl font-bold text-white font-['Playfair_Display'] mb-8">
                                Past Events
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                {pastEvents.slice(0, 6).map((event) => (
                                    <Card key={event.id} className="bg-[#4A5240]/50 border-[#5A6350]">
                                        <CardContent className="p-5">
                                            <p className="text-[#666] text-xs mb-2">
                                                {formatDate(event.date)}
                                            </p>
                                            <h3 className="text-white font-medium">{event.title}</h3>
                                            {event.location && (
                                                <p className="text-[#666] text-sm mt-1">{event.location}</p>
                                            )}
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        </section>
                    )}
                </div>
            </main>

            <MainFooter />
        </div>
    );
};

export default CommunityEventsPage;
